import { Routes } from "@blitzjs/next"
import { useQuery } from "@blitzjs/rpc"
import { SButton, SDropdown } from "@superwall/legacy-ui"
import ApplicationIcon from "app/applications/components/ApplicationIcon"
import { useSession } from "app/authv2/use-session"
import { trpc } from "app/core/trpcClient"
import {
  DisplayBehavior,
  ToolTipHangDirection,
  ToolTipType,
  ToolTipWrapper,
} from "app/core/components/hints"
import useIsMobile from "app/core/hooks/useIsMobile"
import getOrganization from "app/organizations/queries/getOrganization"
import Link from "next/link"
import { useRouter } from "next/router"
import { FC, ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { IoSettingsSharp } from "@superwall/icons/io5/IoSettingsSharp"
import { LogoutButton } from "../LogoutButton"

export type BackButtonBehavior = "back" | "home" | "auto"

export const ApplicationLeftButtons: FC<{
  applicationId: number
  backButtonBehavior?: BackButtonBehavior
  backDestination?: string
  title?: ReactNode | string
}> = ({ applicationId, title, backButtonBehavior = "auto", backDestination }) => {
  const { data: application } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    { suspense: false }
  )
  const router = useRouter()

  const isMobile = useIsMobile()

  // Go back helper
  const goBack = useCallback(() => {
    if (backDestination) {
      router.push(backDestination)
    } else {
      router.back()
    }
  }, [router, backDestination])

  const autoBehavior = useMemo(() => {
    return () => {
      goBack()
      if (!backDestination) {
        router.push(Routes.ShowApplicationDashboardPage({ applicationId }).href)
      }
    }
  }, [applicationId, router, goBack, backDestination])

  const backBehavior = useMemo(() => {
    return goBack
  }, [goBack])

  const homeBehavior = useMemo(() => {
    return () => {
      router.push(Routes.ShowApplicationDashboardPage({ applicationId }).href)
    }
  }, [applicationId, router])

  const pressedBackButton = useMemo(() => {
    switch (backButtonBehavior) {
      case "back":
        return backBehavior
      case "home":
        return homeBehavior
      default:
        return autoBehavior
    }
  }, [backButtonBehavior, autoBehavior, backBehavior, homeBehavior])

  return (
    <>
      {title ? (
        <div className="flex flex-row items-center gap-3">
          <a onClick={pressedBackButton}>
            <div
              className={`flex gap-3 active:scale-[0.97] items-center origin-left justify-start cursor-pointer transition-all ease-in-out`}
            >
              <p className="md:pr-1 text-lg ">←</p>
              {application && (
                <ApplicationIcon size={isMobile ? 22 : 30} application={application!} />
              )}
            </div>
          </a>
          <div className="hidden md:block w-[210px] lg:w-[300px] xl:w-[500px] 2xl:w-[600px] truncate text-left">
            {title}
          </div>
        </div>
      ) : (
        <a onClick={pressedBackButton}>
          <div
            className={`flex gap-3 active:scale-[0.97] items-center origin-left justify-start cursor-pointer transition-all ease-in-out`}
          >
            <p className="md:pr-1 text-lg ">←</p>
            {application && (
              <ApplicationIcon size={isMobile ? 22 : 30} application={application!} />
            )}
            <div className="hidden md:block w-[210px] lg:w-[300px] xl:w-[500px] 2xl:w-[600px] truncate text-left">
              {application?.name || "Home"}
            </div>
          </div>
        </a>
      )}
    </>
  )
}

export const ApplicationSettingsButton: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  return <ApplicationSettings applicationId={applicationId} />
}

export const ApplicationSettings: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  return <MenuDropdownOptions applicationId={applicationId} />
}

enum ApplicationPickerState {
  ORGANIZATION,
  APPLICATION,
  NONE,
}

const MenuDropdownOptions: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  const session = useSession()
  const [appCxtPickerState, setAppCxtPickerState] = useState<ApplicationPickerState>(
    ApplicationPickerState.NONE
  )

  useEffect(() => {
    if (!session) return
    else setAppCxtPickerState(ApplicationPickerState.NONE)
  }, [session])

  return (
    <SDropdown
      title={
        <div className={`py-1`}>
          <IoSettingsSharp />{" "}
        </div>
      }
      isUnstyledItems
      elements={[
        <Link key={`keys`} href={Routes.ApplicationKeysPage({ applicationId })} legacyBehavior>
          <SDropdown.Item>{`Keys`}</SDropdown.Item>
        </Link>,
        <Link
          key={`settings`}
          href={Routes.ApplicationSettingsPage({ applicationId })}
          legacyBehavior
        >
          <SDropdown.Item>{`Settings`}</SDropdown.Item>
        </Link>,

        // Don't show the change organization button if there are no other
        // apps or orgs. If there are, indicate which one
        ...(appCxtPickerState == ApplicationPickerState.NONE
          ? []
          : [
              <Link key={`change-organization`} href={Routes.SelectOrganization()} legacyBehavior>
                <SDropdown.Item>
                  {appCxtPickerState == ApplicationPickerState.ORGANIZATION
                    ? `Change organization`
                    : `Change application`}
                </SDropdown.Item>
              </Link>,
            ]),
        <SDropdown.Item key={`logout`}>
          <LogoutButton />
        </SDropdown.Item>,
      ]}
    />
  )
}

export const ApplicationSettingsWrapper: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  return (
    <Suspense fallback={<ApplicationSettings applicationId={applicationId} />}>
      <ApplicationSettingsButton applicationId={applicationId} />
    </Suspense>
  )
}

export const ApplicationRightButtons: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  const { data: application } = trpc.blitzMigration.applications.getApplication.useQuery(
    { id: applicationId },
    { suspense: false, enabled: !!applicationId }
  )
  const [organization] = useQuery(
    getOrganization,
    { applicationId },
    { suspense: false, enabled: !!applicationId }
  )

  return (
    <>
      {/* <SButton
        className="hidden md:block"
        href={Routes.Dashboard({})}
        variant="secondary"
        isMinimal
        size="lg"
      >
        Apps
      </SButton>
      <SButton
        className="hidden md:block"
        isMinimal
        variant="secondary"
        size="lg"
        href="/docs"
        target="_blank"
        rel="noopener noreferrer"
      >
        Docs
      </SButton> */}
      {/* <ApplicationSettingsWrapper applicationId={applicationId} /> */}
    </>
  )
}

const MobileMenuOptions: FC<{
  applicationId: number
}> = ({ applicationId }) => {
  return (
    <span className="md:hidden">
      <SDropdown
        position="left"
        title={
          <div className={`py-1 `}>
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 20 20"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M3 7a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 13a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              ></path>
            </svg>
          </div>
        }
        isUnstyledItems
        elements={[
          <Link
            key={`dashboard`}
            href={Routes.ShowApplicationDashboardPage({ applicationId: applicationId })}
            legacyBehavior
          >
            <SDropdown.Item>{`Dashboard`}</SDropdown.Item>
          </Link>,
          <Link key={`apps`} href={Routes.Dashboard({})} legacyBehavior>
            <SDropdown.Item>Apps</SDropdown.Item>
          </Link>,
          <Link
            key={`users`}
            href={Routes.UsersPage({ applicationId: applicationId })}
            legacyBehavior
          >
            <SDropdown.Item>Users</SDropdown.Item>
          </Link>,
          <Link
            key={`charts`}
            href={Routes.LegacyChartsRedirect({
              applicationId: applicationId,
            })}
            legacyBehavior
          >
            <SDropdown.Item>Charts</SDropdown.Item>
          </Link>,
          <Link key={`docs`} href="/docs" legacyBehavior>
            <SDropdown.Item>Docs</SDropdown.Item>
          </Link>,
        ]}
      />
    </span>
  )
}
